/* eslint-disable camelcase */
import { getCommonAxiosCommon, UrlBuilder } from 'app/helpers/MiscUtils';
import { NgAPIResponse } from 'app/types';
import axios from 'axios';
import { config } from 'config';
import { BehaviorSubject, Observable } from 'rxjs';

import { Booth, CreateBoothInput } from '../API';
import { VJFPCache } from './CacheUtils';

const EndPoints = {
  BoothByJobFairIdAndBoothId: `${config.vjfpNGRestAPI}/jobfairs/:jobFairId/booths/:boothId`,
  BoothsListByJobFairId: `${config.vjfpNGRestAPI}/jobfairs/:jobFairId/booths`,
  BoothsListByJobFairIdAndByRecruiterId: `${config.vjfpNGRestAPI}/jobfairs/:jobFairId/booths/byRecruiterId/:recruiterId`
};

export class BoothService {
  async updateSortOrder (jobFairId: string, boothId: string, newSortOrder: number) {
    try {
      await axios.put(UrlBuilder(EndPoints.BoothByJobFairIdAndBoothId, { jobFairId, boothId }), {
        sortOrder: newSortOrder
      }, await getCommonAxiosCommon());
    } catch (err) {
      console.log(err);
    }
  }

  async updateBoothInfo (jobFairId: string, boothId: string, description: string, hotJobSummary: string, defaultInterviewDuration: number) {
    try {
      const result = await axios.put<NgAPIResponse<any>>(UrlBuilder(EndPoints.BoothByJobFairIdAndBoothId, { jobFairId, boothId }), {
        description,
        hotJobSummary,
        chatDuration: defaultInterviewDuration * 60000
      }, await getCommonAxiosCommon());

      if (!result.data?.isSuccess) { console.error(result.data.errors); }
    } catch (err) {
      console.log(err);
    }
  }

  async deleteBooth (jobFairId: string, boothId: string) {
    try {
      const result = await axios.delete<NgAPIResponse<any>>(
        UrlBuilder(EndPoints.BoothByJobFairIdAndBoothId, { jobFairId, boothId }),
        await getCommonAxiosCommon()
      );

      if (!result.data?.isSuccess) {
        console.error(result.data.errors);
        return false;
      }

      // Clear cache after deletion
      VJFPCache.removeItem(`BOOTHS_${jobFairId}`);
      return true;
    } catch (err) {
      console.log(err);
      return false;
    }
  }

  async createBooth (jobFairId: string, input: CreateBoothInput): Promise<Booth | undefined> {
    // Clear cache after deletion
    VJFPCache.removeItem(`BOOTHS_${jobFairId}`);
    const result = await axios.post<NgAPIResponse<Booth>>(UrlBuilder(EndPoints.BoothsListByJobFairId, { jobFairId }), input, await getCommonAxiosCommon());
    if (!result.data?.isSuccess) {
      throw new Error((result.data.errors || ['Failed to create booth']).join('. '));
    }
    return result.data.data;
  }

  async updateBooth (jobFairId: string, boothId: string, input: Partial<CreateBoothInput>) {
    // Clear cache after deletion
    VJFPCache.removeItem(`BOOTHS_${jobFairId}`);
    try {
      const result = await axios.put<NgAPIResponse<any>>(UrlBuilder(EndPoints.BoothByJobFairIdAndBoothId, { jobFairId, boothId }), input, await getCommonAxiosCommon());
      if (!result.data?.isSuccess) { console.error(result.data.errors); }
      return input;
    } catch (err) {
      console.log(err);
    }
  }

  async getAllBooths (jobFairId: string): Promise<Booth[]> {
    try {
      const cachedBooths = VJFPCache.getItem(`BOOTHS_${jobFairId}`);
      if (cachedBooths) {
        return cachedBooths;
      }

      const result = await axios.get<NgAPIResponse<Booth[]>>(UrlBuilder(EndPoints.BoothsListByJobFairId, { jobFairId }), await getCommonAxiosCommon());
      if (!result.data?.isSuccess) {
        console.error(result.data.errors);
        return [];
      };

      const vjfpBooths = result?.data.data;

      VJFPCache.setItem(`BOOTHS_${jobFairId}`, vjfpBooths);
      return vjfpBooths;
    } catch (err) {
      console.log(err);
    }
    return [];
  }

  async getBoothByRecruiter (jobFairId: string, recruiterId: number) {
    try {
      const result = await axios.get<NgAPIResponse<Booth>>(UrlBuilder(EndPoints.BoothsListByJobFairIdAndByRecruiterId, { jobFairId, recruiterId }), await getCommonAxiosCommon());
      if (!result.data?.isSuccess) { console.error(result.data.errors); }
      return result.data.data;
    } catch (err) {
      console.log(err);
    }
  }

  async getBoothById (jobFairId: string, boothId: string, logBoothVisit: boolean = false): Promise<Booth> {
    if (!logBoothVisit) {
      const cachedBooths = VJFPCache.getItem(`BOOTHS_${jobFairId}`);
      if (cachedBooths) {
        return cachedBooths.find((x: { id: string; }) => x.id === boothId);
      }
    }
    let url = UrlBuilder(EndPoints.BoothByJobFairIdAndBoothId, { jobFairId, boothId });
    if (logBoothVisit) {
      url += '?logBoothVisit=true';
    }

    const result = await axios.get<NgAPIResponse<Booth>>(url, await getCommonAxiosCommon());
    const booth = result.data.data;
    return booth;
  }

  async getBoothOrSubBoothById (jobFairId: string, boothId: string, recruiterId: number, logBoothVisit: boolean = false) {
    return this.getBoothById(jobFairId, boothId, logBoothVisit);
  }
}

const boothViewCounterSubject = new BehaviorSubject<number>(0);

let _currentBoothViewCounter: number = 0;

export const ActiveBoothViewCounterState = {
  getCurrentBoothViewCounter: (): number => _currentBoothViewCounter,
  getBoothViewCounterObservable: (): Observable<number> => boothViewCounterSubject.asObservable(),
  setBoothViewCounter: (counter: number) => {
    if (counter !== _currentBoothViewCounter) { boothViewCounterSubject.next(counter); }
    _currentBoothViewCounter = counter;
  },
  clearBoothViewCounter: () => {
    boothViewCounterSubject.next(0);
    _currentBoothViewCounter = 0;
  }
};
